import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box, HStack, Text, IconButton, useDisclosure,
  Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton,
} from '@chakra-ui/react';
import { FiMenu } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import CommunicationsHoldBanner from './components/CommunicationsHoldBanner';
import Overview from './pages/Overview';
import StaffManagement from './pages/StaffManagement';
import HotelSettings from './pages/HotelSettings';
import RoomTypeManagement from './pages/RoomTypeManagement';
import RoomManagement from './pages/RoomManagement';
import ChargePresets from './pages/ChargePresets';
import TaxRateManagement from './pages/TaxRateManagement';
import GuestManagement from './pages/GuestManagement';
import CancellationPolicies from './pages/CancellationPolicies';
import AuditLogs from './pages/AuditLogs';
import Packages from './pages/Packages';
import Newsletters from './pages/Newsletters';
import EmailSettings from './pages/EmailSettings';
import BookingEnginePayments from './pages/BookingEnginePayments';
import BookingWidget from './pages/BookingWidget';
import ReminderEmails from './pages/ReminderEmails';
import IcalSync from './pages/IcalSync';
import EmailBookings from './pages/EmailBookings';
import CustomFields from './pages/CustomFields';
import GuestApp from './pages/GuestApp';
import RateModifiers from './pages/RateModifiers';
import RatePlans from './pages/RatePlans';
import ChannelManager from './pages/ChannelManager';
import UserActivity from './pages/UserActivity';
import apiClient from './services/apiClient';
import { applyUserLanguage } from './i18n';

const SIDEBAR_WIDTH = '260px';

function App() {
  const { t } = useTranslation();
  // Seed auth synchronously from localStorage via lazy initialisers rather
  // than in a useEffect. With the effect, the very first paint ran as
  // unauthenticated and its catch-all rewrote the URL to /login — so any
  // deep link or refresh (e.g. landing on /newsletters) was replaced before
  // auth resolved, and the user was then bounced on to Overview.
  const [user, setUser] = useState(() => apiClient.getUser());
  const [isAuthenticated, setIsAuthenticated] = useState(() => apiClient.isAuthenticated());
  const navigate = useNavigate();
  const location = useLocation();
  // Phone (< md): the fixed 260px sidebar becomes a left Drawer behind a
  // hamburger in a top bar. Before this the sidebar stayed fixed at 260px
  // and the content area was offset by it, leaving ~130px of usable width
  // on a 390px phone — every admin page was unusable.
  const drawer = useDisclosure();

  const handleLoginSuccess = (userData) => {
    applyUserLanguage(userData);
    setIsAuthenticated(true);
    setUser(userData);
    navigate('/');
  };

  const handleLogout = () => {
    apiClient.logout();
    setIsAuthenticated(false);
    setUser(null);
    navigate('/login');
  };

  const handleNavigate = (path) => {
    drawer.onClose();
    navigate(path);
  };

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <CommunicationsHoldBanner />
      <Box display="flex" minH="100vh" bg="gray.50">
      {/* Desktop: fixed sidebar */}
      <Box display={{ base: 'none', md: 'block' }}>
        <Sidebar
          activePath={location.pathname}
          onNavigate={handleNavigate}
          user={user}
          onLogout={handleLogout}
        />
      </Box>

      {/* Phone: top bar with hamburger */}
      <HStack
        display={{ base: 'flex', md: 'none' }}
        position="fixed"
        top={0}
        left={0}
        right={0}
        h="52px"
        px={2}
        spacing={2}
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        zIndex="sticky"
      >
        <IconButton
          aria-label={t('common.openMenu')}
          icon={<FiMenu />}
          variant="ghost"
          size="md"
          onClick={drawer.onOpen}
        />
        <Text fontWeight="bold" color="brand.600" noOfLines={1}>
          {user?.user_profile?.hotel?.name || t('common.adminPanel')}
        </Text>
      </HStack>
      <Drawer isOpen={drawer.isOpen} placement="left" onClose={drawer.onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent maxW="280px">
          <DrawerCloseButton zIndex={1} />
          <Sidebar
            activePath={location.pathname}
            onNavigate={handleNavigate}
            user={user}
            onLogout={handleLogout}
            inDrawer
          />
        </DrawerContent>
      </Drawer>

      <Box
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
        flex={1}
        minW={0}
        p={{ base: 3, md: 6 }}
        pt={{ base: '64px', md: 6 }}
        maxW={{ base: '100vw', md: `calc(100vw - ${SIDEBAR_WIDTH})` }}
      >
        <Routes>
          <Route path="/" element={<Overview user={user} />} />
          <Route path="/staff" element={<StaffManagement />} />
          <Route path="/settings" element={<HotelSettings />} />
          <Route path="/room-types" element={<RoomTypeManagement />} />
          <Route path="/rooms" element={<RoomManagement />} />
          <Route path="/charge-presets" element={<ChargePresets />} />
          <Route path="/tax-rates" element={<TaxRateManagement />} />
          <Route path="/guests" element={<GuestManagement />} />
          <Route path="/cancellation-policies" element={<CancellationPolicies />} />
          <Route path="/audit-logs" element={<AuditLogs />} />
          <Route path="/rate-plans" element={<RatePlans />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/newsletters" element={<Newsletters />} />
          <Route path="/email-settings" element={<EmailSettings />} />
          <Route path="/booking-engine-payments" element={<BookingEnginePayments />} />
          <Route path="/booking-widget" element={<BookingWidget />} />
          <Route path="/reminder-emails" element={<ReminderEmails />} />
          <Route path="/ical-sync" element={<IcalSync />} />
          <Route path="/email-bookings" element={<EmailBookings />} />
          <Route path="/custom-values" element={<CustomFields />} />
          <Route path="/guest-app" element={<GuestApp />} />
          <Route path="/rate-modifiers" element={<RateModifiers />} />
          {/* Superuser-only screens (restored 2026-10-24). The route is only
              registered for superusers so a hotel admin cannot reach it; the
              backend enforces IsSuperUser on every endpoint regardless. */}
          {user?.is_superuser && (
            <>
              <Route path="/channel-manager" element={<ChannelManager />} />
              <Route path="/user-activity" element={<UserActivity />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
      </Box>
    </>
  );
}

export default App;
